import * as React from "react";
import { Button } from "@/components/ui/button";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "@/components/ui/input-otp";
import { ArrowLeft, Loader2 } from "lucide-react";
import { loginWithEmail2fa, preLoginWithEmail2fa, AuthResultDto, ApiError } from "@/lib/auth-api";
import { REGEXP_ONLY_DIGITS } from "input-otp";
import { useLanguage } from "@/contexts/LanguageContext";

const VERIFICATION_CODE_SIZE = 6;

interface CodeFormProps {
  email: string;
  password: string;
  onBack: () => void;
  onSuccess: (result: AuthResultDto) => void;
}

export const CodeForm: React.FC<CodeFormProps> = ({ email, password, onBack, onSuccess }) => {
  const [code, setCode] = React.useState("");
  const [isLoading, setIsLoading] = React.useState(false);
  const [isResending, setIsResending] = React.useState(false);
  const [resendSuccess, setResendSuccess] = React.useState(false);
  const [error, setError] = React.useState("");
  const { t } = useLanguage();

  const handleSubmit = async (submittedCode?: string) => {
    const finalCode = submittedCode || code;
    if (finalCode.length !== VERIFICATION_CODE_SIZE) return;

    setIsLoading(true);
    setError("");

    try {
      const result = await loginWithEmail2fa(email, password, finalCode);
      onSuccess(result);
    } catch (err) {
      if (err instanceof ApiError && err.response.status === 401) {
        setError(t("auth.error.invalidCode"));
      } else {
        setError(t("auth.error.generic"));
      }
      setCode("");
    } finally {
      setIsLoading(false);
    }
  };

  const handleResend = async () => {
    setIsResending(true);
    setError("");
    setResendSuccess(false);
    try {
      await preLoginWithEmail2fa(email, password);
      setResendSuccess(true);
      setCode("");
      setTimeout(() => setResendSuccess(false), 3000);
    } catch {
      setError(t("auth.error.generic"));
    } finally {
      setIsResending(false);
    }
  };

  const handleCodeChange = (value: string) => {
    setCode(value);
    if (value.length === VERIFICATION_CODE_SIZE) {
      handleSubmit(value);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <button onClick={onBack} className="hover:bg-secondary rounded-full p-2 transition-colors">
          <ArrowLeft className="text-foreground h-5 w-5" />
        </button>
        <h2 className="text-foreground flex-1 pe-10 text-center text-2xl font-bold">{t("auth.verifyCode.title")}</h2>
      </div>

      <p className="text-muted-foreground text-center text-sm">
        {t("auth.verifyCode.subtitle")} <span className="text-foreground font-medium">{email}</span>
      </p>

      <div className="flex justify-center">
        <InputOTP
          maxLength={VERIFICATION_CODE_SIZE}
          pattern={REGEXP_ONLY_DIGITS}
          value={code}
          onChange={handleCodeChange}
          disabled={isLoading}
          autoFocus>
          <InputOTPGroup>
            {Array.from({ length: VERIFICATION_CODE_SIZE }).map((_, i) => (
              <InputOTPSlot key={i} index={i} className="h-14 w-12 text-xl" />
            ))}
          </InputOTPGroup>
        </InputOTP>
      </div>

      {error && <p className="text-destructive text-center text-sm">{error}</p>}

      {isLoading && (
        <div className="flex justify-center">
          <Loader2 className="h-6 w-6 animate-spin text-primary" />
        </div>
      )}

      <div className="text-center">
        <button
          onClick={handleResend}
          disabled={isResending}
          className="text-sm text-primary hover:underline disabled:opacity-50">
          {isResending ? t("auth.verifyCode.resending") : t("auth.verifyCode.resend")}
        </button>
        {resendSuccess && <p className="mt-1 text-xs text-primary">{t("auth.verifyCode.resendSuccess")}</p>}
        <p className="text-muted-foreground mt-2 text-xs">{t("auth.verifyCode.validFor")}</p>
      </div>
    </div>
  );
};
